import { useCallback, useState } from 'react';
import { Upload, FileIcon } from 'lucide-react';
import { clsx } from 'clsx';

interface FileDropZoneProps {
  onDrop: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  className?: string;
  disabled?: boolean;
}

export function FileDropZone({
  onDrop,
  accept,
  multiple = true,
  maxSize = 10 * 1024 * 1024, // 10MB default
  className,
  disabled = false,
}: FileDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter((file) => {
      if (maxSize && file.size > maxSize) {
        console.warn(`File ${file.name} exceeds max size of ${maxSize} bytes`);
        return false;
      }
      if (accept) {
        const acceptedTypes = accept.split(',').map((t) => t.trim());
        const isValid = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          }
          if (type.endsWith('/*')) {
            return file.type.startsWith(type.slice(0, -1));
          }
          return file.type === type;
        });
        if (!isValid) {
          console.warn(`File ${file.name} type ${file.type} not accepted`);
          return false;
        }
      }
      return true;
    });

    if (!multiple && validFiles.length > 1) {
      onDrop([validFiles[0]]);
    } else {
      onDrop(validFiles);
    }
  }, [disabled, maxSize, accept, multiple, onDrop]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onDrop(Array.from(files));
    }
    e.target.value = '';
  }, [onDrop]);

  return (
    <label
      className={clsx(
        'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
        isDragOver
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex flex-col items-center justify-center pt-5 pb-6">
        {isDragOver ? (
          <FileIcon className="w-8 h-8 mb-2 text-primary-500" />
        ) : (
          <Upload className="w-8 h-8 mb-2 text-gray-400" />
        )}
        <p className="text-sm text-gray-500">
          <span className="font-medium text-primary-600">Click to upload</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">or drag and drop</p>
      </div>
      <input
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        disabled={disabled}
      />
    </label>
  );
}
